import keyCodes from '@honda-canada/js-utilities/lib/keyCodes';
import keypressCallback from '@honda-canada/js-utilities/lib/keypressCallback';
import PropTypes, { shape } from 'prop-types';
import React, { createRef, useCallback, useContext, useEffect } from 'react';
import { Accordion, AccordionExpandAll, Box, Copy, useAccordion } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import Context from './service/Context';
import themeStyles from './TrimSpecifications.styles';
import useGetAccordionItems, { getItemClassName } from './useGetAccordionItems';
import { Print, Screen } from '../../utils/components/ForPrint.styles';

const AccordionsWrapper = themeStyles.apply(Box, 'AccordionsWrapper');
const AccordionsContent = themeStyles.apply(Box, 'AccordionsContent');
const ExpandAllCopy = themeStyles.apply(Copy, 'ExpandAllCopy');

const ITEM_LABEL_CLASS = 'spec-item-label';
const ITEM_VALUE_CLASS = 'spec-item-value';
const SPEC_TO_EXCLUDE = 'legal_disclaimers_';

const Accordions = ({
  refs,
  onDrag,
  // TODO: pass a memoized value for specifications - it currently triggers hooks several times per render
  specifications,
  expandLabel,
  collapseLabel,
  offset,
  itemParentGroup,
  printItemsList,
}) => {
  const { t } = useTranslation();

  const configurationProvider = useContext(Context);
  const { isDark, accordion } = configurationProvider || {};

  const categories = specifications?.filter(({ name }) => name !== SPEC_TO_EXCLUDE);

  // eslint-disable-next-line no-param-reassign
  refs.current = categories?.map((_, i) => refs.current[i] || createRef());

  const { allAccordionItems, allPrintAccordionItems, printGroupKeys, setContentHeights } = useGetAccordionItems({
    refs,
    onDrag,
    specifications,
    itemParentGroup,
    printItemsList,
  });

  const accordionBehaviour = useAccordion(allAccordionItems);
  const accordionPrintBehaviour = useAccordion(allPrintAccordionItems);
  const { isAllExpanded, toggleAllItems, collapseAllItems } = accordionBehaviour;

  /**
   * @type {(element: HTMLElement) => number}
   */
  const getNaturalHeight = useCallback(element => {
    // eslint-disable-next-line no-param-reassign
    element.style.height = 'auto'; /* set the element height to its default size */
    const height = element?.offsetHeight ?? 0; /* read default size (triggers browser repaint) */
    // eslint-disable-next-line no-param-reassign
    element.style.height = ''; /* undo change */
    return height;
  }, []);

  useEffect(() => {
    // Resets expand/collapse state on year change
    collapseAllItems();
    accordionPrintBehaviour.toggleAllItems();
    // TODO: memoize data returned from useAccordion, or they'll trigger react hooks infinitely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemParentGroup]);

  useEffect(() => {
    const specs = specifications?.filter(({ name }) => name !== SPEC_TO_EXCLUDE) ?? [];

    // Set height of each row based on height of its tallest column
    const specsArr = specs.map((category, specIndex) =>
      category?.specificationLabels?.map((_, itemIndex) => {
        const itemLabel = document.querySelector(
          `.${getItemClassName(ITEM_LABEL_CLASS, specIndex, itemIndex, itemParentGroup)}`,
        );
        const itemValues = document.querySelectorAll(
          `.${getItemClassName(ITEM_VALUE_CLASS, specIndex, itemIndex, itemParentGroup)}`,
        );

        let height = 0;

        if (itemLabel) {
          height = getNaturalHeight(itemLabel);
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const itemElem of itemValues) {
          const itemHeight = getNaturalHeight(itemElem);

          if (itemHeight > height) {
            height = itemHeight;
          }
        }

        return `${height}px`;
      }),
    );

    setContentHeights(specsArr);

    return () => {
      setContentHeights([[]]);
    };
    // TODO: memoize data returned from useAccordion, or they'll trigger react hooks infinitely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemParentGroup, setContentHeights, specifications, getNaturalHeight]);

  return (
    <AccordionsWrapper isDark={isDark} data-testid="cy-product-spec-wrapper">
      <Screen>
        <AccordionExpandAll
          data-testid="cy-expand-all"
          toggleItem={toggleAllItems}
          isActive={isAllExpanded}
          onKeyDown={keypressCallback(keyCodes.ENTER, toggleAllItems)}
          colorStyling={isDark ? 'dark' : 'default'}
          justifyContent="flex-end"
          pt="s"
          pb="s"
          borderTop={['solid 1px', 'none']}
          borderColor="grey.3"
          iconColorOverride={accordion?.iconColorOverride}
          ariaLabel={
            isAllExpanded ? t('Shared.CompareTrims.collapseAllLabel') : t('Shared.CompareTrims.expandAllLabel')
          }
        >
          <ExpandAllCopy
            color={accordion?.labelColor}
            aria-label={
              isAllExpanded ? t('Shared.CompareTrims.collapseAllLabel') : t('Shared.CompareTrims.expandAllLabel')
            }
          >
            {isAllExpanded ? collapseLabel : expandLabel}
          </ExpandAllCopy>
        </AccordionExpandAll>
        <AccordionsContent isDark={isDark}>
          <Accordion
            ariaExpanded="false"
            behaviour={accordionBehaviour}
            items={allAccordionItems}
            isCompact
            itemsHaveTopBorder
            isTitleSticky
            titleOffset={offset}
            colorStyling={isDark ? 'dark' : 'default'}
          />
        </AccordionsContent>
      </Screen>
      <Print>
        <AccordionsContent>
          <Accordion
            ariaExpanded="true"
            defaultActiveItems={printGroupKeys}
            behaviour={accordionPrintBehaviour}
            items={allPrintAccordionItems}
            isCompact
            itemsHaveTopBorder
            colorStyling="default"
          />
        </AccordionsContent>
      </Print>
    </AccordionsWrapper>
  );
};

Accordions.propTypes = {
  refs: PropTypes.shape({}),
  onDrag: PropTypes.func,
  specifications: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      specificationLabels: PropTypes.arrayOf(
        shape({
          name: PropTypes.string,
          label: PropTypes.string,
        }),
      ),
      specifications: PropTypes.arrayOf(
        PropTypes.shape({
          itemId: PropTypes.number,
          specs: PropTypes.arrayOf(
            PropTypes.shape({
              displayOrder: PropTypes.number,
              label: PropTypes.string,
              name: PropTypes.string,
              tip: PropTypes.string,
              value: PropTypes.string,
            }),
          ),
        }),
      ),
    }),
  ),
  expandLabel: PropTypes.string,
  collapseLabel: PropTypes.string,
  offset: PropTypes.number,
  itemParentGroup: PropTypes.string,
  printItemsList: PropTypes.arrayOf(PropTypes.element),
};

export default Accordions;
